import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Calendar, Clock, ArrowLeft, Play, Headphones } from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';
import { translations } from '@/lib/translations';
import { Link } from 'react-router-dom';
import { AudioPlayer } from '@/components/AudioPlayer';

interface Episode {
  id: string;
  title: { it: string; es: string; en: string };
  description: { it: string; es: string; en: string };
  audioUrl: string;
  coverImage: string;
  duration: string;
  publishDate: string;
  category: string;
  tags: string[];
}

export function PodcastPage() {
  const { language } = useLanguage();
  const t = translations[language];
  const [episodes, setEpisodes] = useState<Episode[]>([]);
  const [selectedEpisode, setSelectedEpisode] = useState<Episode | null>(null);

  useEffect(() => {
    fetch('/data/podcast-episodes.json')
      .then(response => response.json())
      .then(data => setEpisodes(data.episodes))
      .catch(error => console.error('Error loading episodes:', error));
  }, []);

  if (selectedEpisode) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Back Navigation */}
        <div className="bg-white border-b shadow-sm sticky top-0 z-50">
          <div className="container mx-auto px-4 py-6">
            <button 
              onClick={() => setSelectedEpisode(null)}
              className="inline-flex items-center bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-all duration-200 shadow-md hover:shadow-lg font-medium"
            >
              <ArrowLeft className="w-5 h-5 mr-3" />
              {language === 'it' ? 'Torna ai Podcast' : language === 'es' ? 'Volver a Podcasts' : 'Back to Podcasts'}
            </button>
          </div>
        </div>

        {/* Episode Detail */}
        <div className="py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="relative">
                  <img
                    src={selectedEpisode.coverImage}
                    alt={selectedEpisode.title[language]}
                    className="w-full h-64 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-6 left-6 text-white">
                    <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium mb-4 inline-block">
                      {selectedEpisode.category}
                    </span>
                    <h1 className="text-3xl font-bold mb-2">{selectedEpisode.title[language]}</h1>
                    <div className="flex items-center space-x-4 text-sm">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        <span>{new Date(selectedEpisode.publishDate).toLocaleDateString(language === 'it' ? 'it-IT' : language === 'es' ? 'es-ES' : 'en-US')}</span>
                      </div>
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        <span>{selectedEpisode.duration}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="p-8">
                  <p className="text-gray-700 text-lg mb-8 leading-relaxed">
                    {selectedEpisode.description[language]}
                  </p>
                  
                  <AudioPlayer 
                    audioUrl={selectedEpisode.audioUrl}
                    title={selectedEpisode.title[language]}
                    coverImage={selectedEpisode.coverImage}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Back Navigation */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <Link 
            to="/" 
            className="inline-flex items-center text-gray-600 hover:text-blue-600 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {language === 'it' ? 'Torna alla Home' : language === 'es' ? 'Volver al Inicio' : 'Back to Home'}
          </Link>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 text-white py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              {t.podcast.title}
            </h1>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto">
              {t.podcast.subtitle}
            </p>
          </div>
        </div>
      </div>

      {/* Podcast Episodes */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {episodes.map((episode) => (
                <div
                  key={episode.id}
                  className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 group cursor-pointer"
                  onClick={() => setSelectedEpisode(episode)}
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={episode.coverImage}
                      alt={episode.title[language]}
                      className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                      <div className="bg-white/90 rounded-full p-3 group-hover:bg-blue-600 group-hover:text-white transition-all">
                        <Play className="w-6 h-6" />
                      </div>
                    </div>
                    <div className="absolute top-4 left-4">
                      <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                        {episode.category}
                      </span>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <h3 className="text-xl font-bold mb-3 text-gray-900 group-hover:text-blue-600 transition-colors line-clamp-2">
                      {episode.title[language]}
                    </h3>
                    
                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {episode.description[language]}
                    </p>
                    
                    <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          <span>{new Date(episode.publishDate).toLocaleDateString(language === 'it' ? 'it-IT' : language === 'es' ? 'es-ES' : 'en-US')}</span>
                        </div>
                        <div className="flex items-center">
                          <Clock className="w-4 h-4 mr-1" />
                          <span>{episode.duration}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Headphones className="w-4 h-4 mr-2 text-gray-400" />
                        <span className="text-sm text-gray-600">Podcast</span>
                      </div>
                      
                      <Button 
                        type="button"
                        variant="outline" 
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedEpisode(episode);
                        }}
                      >
                        <Play className="w-4 h-4 mr-2" />
                        {language === 'it' ? 'Ascolta' : language === 'es' ? 'Escuchar' : 'Listen'}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// Default export for lazy loading
export default PodcastPage;